import {
  ChangeDetectorRef,
  Component,
  TemplateRef,
  ViewContainerRef,
  OnInit,
  ViewChild,
  ViewEncapsulation,
  ElementRef,
  AfterViewInit,
} from '@angular/core';
import { ActivatedRoute, Data, Router } from '@angular/router';
import { noop, Observable, of } from 'rxjs';
import { MatLegacyMenu as MatMenu } from '@angular/material/legacy-menu';
import { switchMap, filter, tap, debounceTime, map } from 'rxjs/operators';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import cloneDeep from 'lodash-es/cloneDeep';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { MatLegacyButton as MatButton } from '@angular/material/legacy-button';
import { TemplatePortal } from '@angular/cdk/portal';
import { MatLegacyCheckboxChange as MatCheckboxChange } from '@angular/material/legacy-checkbox';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { Select, Store } from '@ngxs/store';

import { ProjectsService } from 'app/modules/projects/projects.service';
import { Pipeline, Project, ProjectTemplate } from 'app/modules/projects/projects.types';
import { ProjectMarkAcquiredComponent } from 'app/modules/projects/modals/project-mark-acquired/project-mark-acquired.component';
import { ProjectChangeTemplateComponent } from 'app/modules/projects/modals/project-change-template/project-change-template.component';
import { ProjectShowDealEmailComponent } from 'app/modules/projects/modals/project-show-deal-email/project-show-deal-email.component';
import { AccountService, AuthService } from 'app/core';
import { Account } from 'app/core/models/account.types';
import { CalculatorModalComponent } from 'app/layout/common/calculator/calculator-modal/calculator-modal.component';
import { GenerateMergeDocumentComponent } from 'app/shared/modals/generate-merge-document/generate-merge-document.component';
import { ProjectGenerateUnderwritingModelComponent } from 'app/modules/projects/modals/project-generate-underwriting-model/project-generate-underwriting-model.component';
import { ProjectSharingComponent } from '../../modals/project-sharing/project-sharing.component';
import { FieldDef, User, UserAssociation } from 'app/core/models';
import { ProjectEditPipelineComponent } from '../../modals/project-edit-pipeline/project-edit-pipeline.component';
import { UserService } from 'app/layout/common/user/user.service';
import { InviteUserComponent } from 'app/modules/settings/modals/invite-user/invite-user.component';
import { FeatureFlagService } from 'app/core/services/feature-flag.service';
import { FeatureFlag } from 'app/core/enums/feature-flag';
import { AccountIntegrationSlug, FileFormat, RelatedToType } from 'app/shared/enums';
import { generateDownloadableFile } from 'app/shared/functions';
import { FoldersTabService } from 'app/shared/modules/folders/folders.service';
import { FileResource } from 'app/shared/modules/folders/folders.types';
import { CustomObject } from 'app/shared/modules/custom-objects/custom-objects.types';
import { ObjectsManagerState } from 'app/modules/settings/objects-manager/state/state';
import { ObjectsManagerActions } from 'app/modules/settings/objects-manager/state/actions';
import { ConfirmService } from 'app/shared/components/widgets/dialog/confirm/confirm.service';
import { Module, NewPermission, Permission } from 'app/core/enums';
import { guid } from 'app/shared/Utils/common.utils';
import { AccountState } from '@state/account/state';
import { UserState } from '@state/user/state';
import { EthanAIAbilitiesService } from '@services/ethan-ai-abilities.service';
import { EthanFormFieldSelectionModal } from 'app/shared/modals/ethan-form-field-selection-modal/ethan-form-field-selection-modal.component';
import { Dialog } from '@angular/cdk/dialog';
import { hideCustomObjectTabBasedOnArchivedLinkedField } from '@shared/modules/custom-objects/custom-objects.functions';
import { TaskState } from '@tasks/state/state';

@Component({
  selector: 'project-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ProjectsHeaderComponent extends OnDestroyMixin implements OnInit, AfterViewInit {
  project: Project;
  @ViewChild('matMenu', { static: true }) matMenu: MatMenu;

  private _userAssociationsPanelOverlayRef: OverlayRef;
  @ViewChild('userAssociationsPanel')
  private _userAssociationsPanel: TemplateRef<any>;
  @ViewChild('userAssociationsPanelOrigin')
  private _userAssociationsPanelOrigin: MatButton;
  private _userAssociationsPanelTemplatePortal: TemplatePortal;

  @ViewChild('headerContainer') private headerContainer: ElementRef;
  @ViewChild('rightContainer') private rightContainer: ElementRef;
  @ViewChild('menuContainer') private menuContainer: ElementRef;
  @ViewChild('leftContainer') private leftContainer: ElementRef;

  editMode: boolean = false;
  data: any;
  fieldDefs: FieldDef[];
  account: Account;
  isCurrentUserAdmin: boolean = false;
  currentUser: User;
  exportingPdf: boolean = false;
  Permission = Permission;
  NewPermission = NewPermission;
  projects$: Observable<Project[]>;
  projectList: number[];
  currentProjectIndex: number;
  prev: number;
  next: number;
  accountUserAssociations: UserAssociation[];
  userAssociations: UserAssociation[];
  filteredUserAssociations: UserAssociation[] = [];
  projectUserIds: any[] = [];

  userRole: string;
  procoreEnabled: boolean = false;
  projectTemplate: ProjectTemplate<any>;
  projectRoute: string;
  projectPipelineRoute: any;
  tabs: any = [];
  currentTabId: any;

  isPortfolioLoading: boolean = false;
  isPortfolio: boolean = false;
  ui = { isAiLoading: false };

  pipeline: Pipeline;

  relatedToTypeProject = RelatedToType.Project;
  customObjectsTabs: CustomObject[] = [];
  visibleCustomObjectsTabs: CustomObject[] = [];
  hiddenCustomObjectsTabs: CustomObject[] = [];
  selectedCustomObjectTab: CustomObject;
  hasDropDown = false;
  newPermissionEnabled = false;

  get canAccessDispositions$(): Observable<boolean> {
    return this._featureFlagService.hasFeatureFlag(FeatureFlag.DISPOSITIONS_TAB).pipe(
      map((hasFeature) => {
        const newPermissionsEnabled = this.store.selectSnapshot(
          AccountState.isFeatureEnabled(FeatureFlag.NEW_PERMISSIONS),
        );

        if (!hasFeature) {
          return false;
        }

        return newPermissionsEnabled ? this.authService.hasAnyPermissionInModule(Module.DISPOSITIONS) : true;
      }),
    );
  }

  get hasLoanQuotesEnabled$(): Observable<boolean> {
    return this._featureFlagService.hasFeatureFlag(FeatureFlag.LOAN_QUOTES).pipe(
      map((hasFeature) => {
        const newPermissionsEnabled = this.store.selectSnapshot(
          AccountState.isFeatureEnabled(FeatureFlag.NEW_PERMISSIONS),
        );

        if (!hasFeature) {
          return false;
        }

        return newPermissionsEnabled ? this.authService.hasAnyPermissionInModule(Module.LOAN_QUOTES) : true;
      }),
    );
  }

  get hasCustomObjectsEnabled$(): Observable<boolean> {
    return this._featureFlagService.hasFeatureFlag(FeatureFlag.CUSTOM_OBJECTS);
  }

  get hasAccessArchiveProject$(): Observable<boolean> {
    return this._featureFlagService.hasFeatureFlag(FeatureFlag.ARCHIVE_PROJECT);
  }

  get isArchived(): boolean {
    return !!this.project.archived_at;
  }

  @Select(ObjectsManagerState.get) get$: Observable<CustomObject[]>;

  constructor(
    private _projectsService: ProjectsService,
    private _userService: UserService,
    private _router: Router,
    private _snackBar: MatSnackBar,
    private _matDialog: MatDialog,
    private dialog: Dialog,
    private _overlay: Overlay,
    private _viewContainerRef: ViewContainerRef,
    private _changeDetectorRef: ChangeDetectorRef,
    private _activatedRoute: ActivatedRoute,
    private _accountService: AccountService,
    private _featureFlagService: FeatureFlagService,
    private readonly foldersTabService: FoldersTabService,
    private readonly store: Store,
    private readonly confirmService: ConfirmService,
    private readonly authService: AuthService,
    private readonly ethanAbilities: EthanAIAbilitiesService,
  ) {
    super();

    this._activatedRoute.params.pipe(untilComponentDestroyed(this)).subscribe((params) => {
      this.currentTabId = params.tab_id;
    });
  }

  ngOnInit(): void {
    this.newPermissionEnabled = this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_PERMISSIONS));

    this._projectsService.projects$.pipe(untilComponentDestroyed(this)).subscribe((projects: Project[]) => {
      if (projects) {
        this.projectList = projects.map((i) => +i.id);
      }
    });

    this._projectsService.projectTemplate$
      .pipe(untilComponentDestroyed(this))
      .subscribe((projectTemplate: ProjectTemplate<any>) => {
        if (projectTemplate) {
          this.projectTemplate = projectTemplate;

          this.tabs = this.getTabsFromTemplate(projectTemplate);
          this._changeDetectorRef.markForCheck();
        }
      });

    this._projectsService.project$.pipe(untilComponentDestroyed(this)).subscribe((project: Project) => {
      this.project = project;
      this.setProjectRoute();

      if (this.project.child_ids?.length) this.isPortfolio = true;

      if (this.projectList) {
        this.currentProjectIndex = this.projectList.findIndex((i) => i === +project.id);
        this.prev = this.projectList[this.currentProjectIndex - 1];
        this.next = this.projectList[this.currentProjectIndex + 1];
      }

      // Mark for check
      this._changeDetectorRef.markForCheck();
    });

    this._activatedRoute.data.pipe(untilComponentDestroyed(this)).subscribe((data: Data) => {
      this.data = data;
      this.fieldDefs = this.data.fieldDefs.filter((field) => field.related_to_type == 'Project');
    });

    this._accountService.currentAccount.pipe(untilComponentDestroyed(this)).subscribe((accountData) => {
      this.account = accountData;
    });

    this._accountService
      .getAccountIntegrations()
      .pipe(untilComponentDestroyed(this))
      .subscribe((accountIntegrations) => {
        this.procoreEnabled =
          accountIntegrations.filter((ai) => ai.integration.slug === AccountIntegrationSlug.Procore && ai.active)
            .length > 0;
      });

    this._accountService.userAssociations$
      .pipe(untilComponentDestroyed(this))
      .subscribe((userAssociations: UserAssociation[]) => {
        this.accountUserAssociations = userAssociations;
        this.filteredUserAssociations = userAssociations;
        this.checkIfAdmin();
      });

    this._projectsService.userAssociations$
      .pipe(untilComponentDestroyed(this))
      .subscribe((userAssociations: UserAssociation[]) => {
        if (userAssociations) {
          this.userAssociations = userAssociations;
          this.projectUserIds = this.userAssociations.map((ua) => ua.user.id);
        }
        this._changeDetectorRef.markForCheck();
      });

    this._userService.role$.pipe(untilComponentDestroyed(this)).subscribe((role: string) => {
      if (role) {
        this.userRole = role;
      }
      this._changeDetectorRef.markForCheck();
    });

    this._userService.user$.pipe(untilComponentDestroyed(this)).subscribe((user: User) => {
      if (user) {
        this.currentUser = user;
        this.checkIfAdmin();
      }
      this._changeDetectorRef.markForCheck();
    });

    this._projectsService.changePanelState$.pipe(untilComponentDestroyed(this)).subscribe((state) => {
      this.editMode = state;
    });

    this.onCreateCustomObjectsTabs();

    this._projectsService.pipeline$.pipe(untilComponentDestroyed(this)).subscribe((pipeline: Pipeline) => {
      if (pipeline) {
        this.pipeline = pipeline;
      }
    });
  }

  ngAfterViewInit(): void {
    window.onresize = () => {
      this.handleCOLinkMenuResponsiveness();
    };
  }

  handleCOLinkMenuResponsiveness(): void {
    if (!this.headerContainer) return;

    const headerWidth = this.headerContainer.nativeElement.offsetWidth * 0.7;

    const rightWidth = [...this.rightContainer.nativeElement.children]?.reduce((acc, child) => {
      return acc + child.offsetWidth;
    }, 0);

    const menuWidth = [...this.menuContainer.nativeElement.children]?.reduce((acc, child) => {
      return acc + child.offsetWidth;
    }, 0);

    const leftWidth = [...this.leftContainer.nativeElement.children]?.reduce((acc, child) => {
      return acc + child.offsetWidth;
    }, 0);

    const availableWidth = headerWidth - rightWidth - menuWidth - leftWidth;

    const customTabsWidth = this.customObjectsTabs?.reduce((acc, tab) => {
      return acc + tab.name.length * 10;
    }, 0);

    if (customTabsWidth > availableWidth) {
      this.hasDropDown = true;
      let remainingWidth = availableWidth;
      this.visibleCustomObjectsTabs = this.customObjectsTabs.filter((tab) => {
        if (remainingWidth > tab.name.length * 10) {
          remainingWidth -= tab.name.length * 10;
          return true;
        }
        return false;
      });

      this.hiddenCustomObjectsTabs = this.customObjectsTabs.filter((tab) => {
        return !this.visibleCustomObjectsTabs.includes(tab);
      });
    } else {
      this.hasDropDown = false;
      this.visibleCustomObjectsTabs = this.customObjectsTabs;
    }

    if (this.hiddenCustomObjectsTabs?.length) {
      this.selectCustomObjectTab(this.hiddenCustomObjectsTabs[0]);
    }

    this._changeDetectorRef.markForCheck();
  }

  setProjectRoute(): void {
    this.projectRoute = '/projects/' + this.project.pipeline.id + '/' + this.project.id;
    this.projectPipelineRoute = '/projects/' + this.project.pipeline.id;
  }

  saveTemplate(): void {
    const template = cloneDeep(this.projectTemplate);
    template.definition = this.projectTemplate.definition;
    this._projectsService
      .updateProjectTemplate(template)
      .pipe(untilComponentDestroyed(this))
      .subscribe((response) => {
        this.projectTemplate = response;
        this.tabs = this.getTabsFromTemplate(response);
        this._changeDetectorRef.markForCheck();
      });
  }

  goToPrevious(): void {
    this._router.navigate(['../../' + this.prev], { relativeTo: this._activatedRoute });
  }

  goToNext(): void {
    this._router.navigate(['../../' + this.next], { relativeTo: this._activatedRoute });
  }

  checkIfAdmin(): void {
    if (this.currentUser && this.accountUserAssociations) {
      const foundUserAssociations = this.accountUserAssociations.filter((ua) => {
        return ua.user.id == this.currentUser.id;
      });
      if (foundUserAssociations && foundUserAssociations.length > 0) {
        this.isCurrentUserAdmin = true;
      }
    }
  }

  addPanel(panelDef): void {
    const clonedPanelDef = cloneDeep(panelDef);
    this._projectsService.addPanelDefinition(clonedPanelDef);
  }

  shareProject(): void {
    const dialogRef = this._matDialog.open(ProjectSharingComponent, {
      panelClass: 'mailbox-compose-dialog',
      data: { project: this.project },
      width: '40vw',
    });

    dialogRef.afterClosed().pipe(untilComponentDestroyed(this)).subscribe(noop);
  }

  toggleFavorite(): void {
    this._userService
      .toggleFavoritesRecord(this.account.id, 'Project', this.project.id)
      .pipe(untilComponentDestroyed(this))
      .subscribe(() => {
        this._changeDetectorRef.markForCheck();
      });
  }

  get isFavorited(): boolean {
    return this._userService.isFavorited('Project', this.project.id);
  }

  toggleEditMode(cancel = false) {
    this.editMode = !this.editMode;
    this._projectsService.changePanelLayout(!cancel ? this.editMode : null);
  }

  openCalculatorModal(): void {
    const dialogRef = this._matDialog.open(CalculatorModalComponent, {
      data: { project: this.project },
    });

    dialogRef.afterClosed().pipe(untilComponentDestroyed(this)).subscribe(noop);
  }

  duplicateProject(): void {
    this.confirmService
      .openConfirmationModal({
        title: 'Duplicate Confirmation',
        message: `Are you sure that you wish to duplicate the deal ${this.project.title}?`,
      })
      .pipe(
        filter((res: boolean) => res),
        switchMap(() => {
          const id = parseInt(this.project.id);
          return this._projectsService.duplicateProject(id);
        }),
        untilComponentDestroyed(this),
      )
      .subscribe((res) => {
        this._snackBar.open(`New deal ${res.title} has been created`, '', {
          duration: 3000,
        });

        this._router.navigate(['projects', res.pipeline_id, res.id, 'details', 'overview']);
      });

    this._changeDetectorRef.markForCheck();
  }

  archiveProject(): void {
    this.confirmService
      .openConfirmationModal({
        title: 'Archive Confirmation',
        message: `Are you sure that you wish to archive the deal ${this.project.title}?`,
      })
      .pipe(
        filter((res: boolean) => res),
        switchMap(() => {
          const id = parseInt(this.project.id);
          return this._projectsService.archiveProject(id).pipe(untilComponentDestroyed(this));
        }),
      )
      .subscribe((res) => {
        this._projectsService.setNextProjectValue(res);
        this._snackBar.open(`Deal ${res.title} has been archived`, '', {
          duration: 3000,
        });
      });

    this._changeDetectorRef.markForCheck();
  }

  unarchiveProject(): void {
    this.confirmService
      .openConfirmationModal({
        title: 'Unarchive Confirmation',
        message: `Are you sure that you wish to unarchive the deal ${this.project.title}?`,
      })
      .pipe(
        filter((res: boolean) => res),
        switchMap(() => {
          const id = parseInt(this.project.id);
          return this._projectsService.unarchiveProject(id).pipe(untilComponentDestroyed(this));
        }),
      )
      .subscribe((res) => {
        this._projectsService.setNextProjectValue(res);
        this._snackBar.open(`Deal ${res.title} has been unarchived`, '', {
          duration: 3000,
        });
        this._changeDetectorRef.markForCheck();
      });
  }

  deleteProject(): void {
    const projectHasChildren = this.project.children && this.project.children.length > 0;
    const message = `Are you sure that you wish to delete the deal ${this.project.title}?${
      projectHasChildren ? '\nThis will delete all related child deals as well.' : ''
    }`;

    this.confirmService
      .openConfirmationModal({ message })
      .pipe(filter((res: boolean) => res))
      .subscribe(() => {
        const id = this.project.id;
        this._projectsService
          .deleteProject(id)
          .pipe(untilComponentDestroyed(this))
          .subscribe(() => {
            const route = this._activatedRoute;
            this._userService.getRecentProjects(this.account.id).pipe(untilComponentDestroyed(this)).subscribe();
            this._router.navigate(['../../'], { relativeTo: route });
            this._changeDetectorRef.markForCheck();
          });
      });
  }

  markAcquired(): void {
    this._matDialog
      .open(ProjectMarkAcquiredComponent, {
        panelClass: 'mailbox-compose-dialog',
        data: { project: this.project, options: this.data.options },
      })
      .afterClosed()
      .pipe(untilComponentDestroyed(this))
      .subscribe(noop);
  }

  changeProjectTemplate(): void {
    this._matDialog
      .open(ProjectChangeTemplateComponent, {
        panelClass: 'mailbox-compose-dialog',
        data: {
          template_id: this.project.template?.id,
          options: this.data.options,
          related_to_type: RelatedToType.Project,
        },
      })
      .afterClosed()
      .pipe(
        untilComponentDestroyed(this),
        switchMap((result) => {
          if (result && result?.template_id) {
            const options = {
              template_id: result?.template_id,
              id: parseInt(this.project.id),
            };
            return this._projectsService.saveProject(options, null, false);
          }
          return of(null);
        }),
      )
      .subscribe((project) => {
        if (!project) return;
        window.location.assign(
          window.location.origin + '/projects/' + project.pipeline_id + '/' + project.id + '/details/overview',
        );
      });
  }

  changePipeline(): void {
    const dialogRef = this._matDialog.open(ProjectEditPipelineComponent, {
      panelClass: 'mailbox-compose-dialog',
      data: {
        project: this.project,
        pipelines: this.data.options.pipelines,
        currentPipeline: this.pipeline,
        data: this.data,
        hideTemplateOption: true,
      },
    });

    dialogRef
      .afterClosed()
      .pipe(untilComponentDestroyed(this))
      .subscribe((result) => {
        if (result && result.moved) {
          this._snackBar.open('Deal has been moved.', '', {
            duration: 3000,
          });
          this._changeDetectorRef.markForCheck();
        }
      });
  }

  showSmartEmail(): void {
    //show modal
    const dialogRef = this._matDialog.open(ProjectShowDealEmailComponent, {
      panelClass: 'mailbox-compose-dialog',
      data: { project: this.project },
    });

    dialogRef.afterClosed().pipe(untilComponentDestroyed(this)).subscribe(noop);
  }

  ngOnDestroy(): void {
    if (this._userAssociationsPanelOverlayRef) {
      this._userAssociationsPanelOverlayRef.dispose();
    }
    super.ngOnDestroy();
  }

  filterUserAssociations(event): void {
    // Get the value
    const value = event.target.value.toLowerCase();

    // Filter the tags
    this.filteredUserAssociations = this.accountUserAssociations.filter((ua) =>
      ua.user.full_name?.toLowerCase().includes(value),
    );
  }

  exportProject(format: string = 'pdf'): void {
    this.exportingPdf = true;
    this._snackBar.open('Download will begin shortly.', '', {
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
    });
    this._projectsService
      .getProjectById(this.project.id, format)
      .pipe(untilComponentDestroyed(this))
      .subscribe((data) => {
        if (data) {
          if ([FileFormat.PDF, FileFormat.CSV].includes(format as FileFormat)) {
            generateDownloadableFile(data, format as FileFormat, `${this.project.title}`);
          }
          this.exportingPdf = false;
        }
      });
  }

  convertToPortfolio(): void {
    this.confirmService
      .openConfirmationModal({
        title: 'Confirmation',
        message: 'Are you sure you want to turn this deal into a portfolio?',
      })
      .pipe(filter((res: boolean) => res))
      .subscribe(() => {
        this._snackBar.open('Please be patient while we are converting to a portfolio.', '', {
          duration: 6000,
          horizontalPosition: 'center',
          verticalPosition: 'bottom',
        });
        this.turnIntoPortfolio();
      });
  }

  private turnIntoPortfolio(): void {
    this.isPortfolioLoading = true;

    const newPorftolio = {
      title: this.project.title,
      pipeline_id: this.project.pipeline.id,
      template_id: this.project.template_id,
      child_ids: [this.project.id],
    };

    this._projectsService
      .saveProject(newPorftolio, null, false)
      .pipe(
        untilComponentDestroyed(this),
        switchMap((portfolio: Project) => {
          const uploadedFile = this.data.file;
          if (uploadedFile) {
            this.foldersTabService.moduleId = portfolio.id;
            this.foldersTabService.moduleBaseUrl = 'projects';

            return this.foldersTabService
              .createFile(portfolio.root_folder_resource_id, {
                folder_resource_id: portfolio.root_folder_resource_id,
                name: uploadedFile.name,
              })
              .pipe(
                switchMap((fileResource: FileResource) =>
                  this.foldersTabService.uploadDocument(fileResource.folder_resource_id, fileResource.id, uploadedFile),
                ),
                switchMap(() => of(portfolio)),
              );
          }

          return of(portfolio);
        }),
      )
      .subscribe((portfolio: Project) => {
        this.isPortfolioLoading = false;
        this._snackBar.open('Conversion successful.', '', {
          duration: 3000,
          horizontalPosition: 'center',
          verticalPosition: 'bottom',
        });
        this._router.navigateByUrl(`/projects/${portfolio.pipeline_id}/${portfolio.id}`);
      });
  }

  generateProjectPdf(format: string = 'pdf', fileResourceId: number = null, fileName: string = null): void {
    this.exportingPdf = true;
    this._snackBar.open('Download will begin shortly.', '', {
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
    });
    this._projectsService
      .generateProjectDocument(this.project.id, format, fileResourceId)
      .pipe(untilComponentDestroyed(this))
      .subscribe((response) => {
        if (response.data) {
          if ([FileFormat.PDF, FileFormat.DOCX, FileFormat.PPTX].includes(format as FileFormat)) {
            generateDownloadableFile(response.data, format as FileFormat, `${this.project.title} - ${fileName}`);
          }
          this.exportingPdf = false;
        }
      });
  }

  generateDocusign(format: string = 'pdf', fileResourceId: number = null): void {
    this.exportingPdf = true;
    this._snackBar.open('Docusign will open shortly.', '', {
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
    });

    this._projectsService
      .generateProjectDocument(this.project.id, format, fileResourceId, true)
      .pipe(untilComponentDestroyed(this))
      .subscribe((response) => {
        if (response.data) {
          if (response.url) {
            window.open(response.url, '_blank');
          } else {
            if ([FileFormat.PDF, FileFormat.DOCX, FileFormat.PPTX].includes(format as FileFormat)) {
              generateDownloadableFile(response.data, format as FileFormat, `${this.project.title}`);
            }
          }
          this.exportingPdf = false;
        }
      });
  }

  generateUnderwritingModel(fileResourceId: number = null, fileName: string, reforecast: boolean = false): void {
    this.exportingPdf = true;
    this._snackBar.open('Model will save to your folders and download shortly.', '', {
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
    });

    this._projectsService
      .generateProjectUnderwritingDocument(this.project.id, fileResourceId, reforecast)
      .pipe(untilComponentDestroyed(this))
      .subscribe((response) => {
        if (response.document) {
          if (response?.document?.original_url) {
            if (reforecast) {
              this._snackBar.open('Reforecast completed successfully.', 'Ok');
            } else {
              this._snackBar
                .open('Model built successfully.', 'Download', {
                  horizontalPosition: 'center',
                  verticalPosition: 'bottom',
                })
                .onAction()
                .pipe(untilComponentDestroyed(this))
                .subscribe(() => {
                  window.open(response?.document?.original_url);
                });
            }
          }
        }
      });
  }

  openGenerateModal(): void {
    const dialogRef = this._matDialog.open(GenerateMergeDocumentComponent, {
      panelClass: 'mailbox-compose-dialog',
      data: { project: this.project },
      width: '40vw',
    });

    dialogRef
      .afterClosed()
      .pipe(untilComponentDestroyed(this))
      .subscribe((result) => {
        if (result && result.file_resource_id) {
          if (result.action && result.action === 'generate') {
            this.generateProjectPdf(result.format, result.file_resource_id, result.file_name);
          } else if (result.action && result.action === 'docusign') {
            this.generateDocusign(result.format, result.file_resource_id);
          }
        }
      });
  }

  openGenerateUnderwritingModal(reforecast: boolean = false): void {
    const dialogRef = this._matDialog.open(ProjectGenerateUnderwritingModelComponent, {
      panelClass: 'mailbox-compose-dialog',
      data: { project: this.project, header: reforecast ? 'Reforecast' : 'Generate Underwriting Model' },
      width: '40vw',
    });

    dialogRef
      .afterClosed()
      .pipe(untilComponentDestroyed(this))
      .subscribe((result) => {
        if (result && result.file_resource_id) {
          if (result.action && result.action == 'generate') {
            this.generateUnderwritingModel(result.file_resource_id, result.file_name, reforecast);
          }
        }
      });
  }

  openUserAssociationsPanel(): void {
    this._userAssociationsPanelOverlayRef = this._overlay.create({
      backdropClass: '',
      hasBackdrop: true,
      scrollStrategy: this._overlay.scrollStrategies.block(),
      positionStrategy: this._overlay
        .position()
        .flexibleConnectedTo(this._userAssociationsPanelOrigin._elementRef.nativeElement)
        .withFlexibleDimensions()
        .withViewportMargin(64)
        .withLockedPosition()
        .withPositions([
          {
            originX: 'start',
            originY: 'bottom',
            overlayX: 'start',
            overlayY: 'top',
          },
        ]),
    });

    this._userAssociationsPanelOverlayRef
      .attachments()
      .pipe(untilComponentDestroyed(this))
      .subscribe(() => {
        this._userAssociationsPanelOverlayRef.overlayElement.querySelector('input').focus();
      });

    this._userAssociationsPanelTemplatePortal = new TemplatePortal(this._userAssociationsPanel, this._viewContainerRef);

    this._userAssociationsPanelOverlayRef.attach(this._userAssociationsPanelTemplatePortal);

    this._userAssociationsPanelOverlayRef
      .backdropClick()
      .pipe(untilComponentDestroyed(this))
      .subscribe(() => {
        this._closeUserAssociationsPanel();
      });
  }

  private _closeUserAssociationsPanel(): void {
    if (this._userAssociationsPanelOverlayRef && this._userAssociationsPanelOverlayRef.hasAttached()) {
      this._userAssociationsPanelOverlayRef.detach();
      this.filteredUserAssociations = this.accountUserAssociations;
    }

    if (this._userAssociationsPanelTemplatePortal && this._userAssociationsPanelTemplatePortal.isAttached) {
      this._userAssociationsPanelTemplatePortal.detach();
    }
  }

  toggleUserAssociation(userAssociation: any, change: MatCheckboxChange): void {
    if (change.checked) {
      this.addUserAssociation(userAssociation);
    } else {
      this.removeUserAssociation(userAssociation);
    }
  }

  filterUserAssociationsInputKeyDown(event): void {
    // Return, if the pressed key is not 'Enter'
    if (event.key !== 'Enter') {
      return;
    }

    // If there is a tag...
    const ua = this.filterUserAssociations[0];
    const isFieldVisible = this.userAssociations.find((item) => item.user.full_name === ua.user.full_name);

    // If the found tag is already applied to the contact...
    if (isFieldVisible) {
      // Remove the tag from the contact
      this.removeUserAssociation(ua);
    } else {
      // Otherwise add the tag to the contact
      this.addUserAssociation(ua);
    }
  }

  removeUserAssociation(userAssociation): void {
    const index = this.userAssociations.findIndex((item) => item.user.id === userAssociation.user.id);
    const ua = this.userAssociations[index];
    this._projectsService.deleteUserAssociation(ua, this.project.id).pipe(untilComponentDestroyed(this)).subscribe();
  }

  addUserAssociation(userAssociation): void {
    this._projectsService
      .createUserAssociation(userAssociation.user, this.project.id)
      .pipe(untilComponentDestroyed(this))
      .subscribe();
  }

  openInvitationModal(): void {
    this._closeUserAssociationsPanel();

    const dialogRef = this.dialog.open(InviteUserComponent, {
      panelClass: 'mailbox-compose-dialog',
      data: { role: 'guest', extra: { project_id: this.project.id } },
    });

    dialogRef.closed.pipe(untilComponentDestroyed(this)).subscribe(noop);
  }

  syncToProcore(): void {
    this._projectsService
      .syncProjectWithProcore(this.project.id)
      .pipe(untilComponentDestroyed(this))
      .subscribe((project) => {
        this._snackBar
          .open('The deal has been synced with Procore.', 'View in Procore', {
            duration: 3000,
            horizontalPosition: 'center',
            verticalPosition: 'bottom',
          })
          .onAction()
          .pipe(untilComponentDestroyed(this))
          .subscribe(() => {
            window.open(`https://sandbox.procore.com/${project.procore_project_id}/project/home`);
          });
      });
  }

  /**
   * Track by function for ngFor loops
   *
   * @param index
   * @param item
   */
  trackByFn(index: number, item: any): any {
    return item.id || index;
  }

  hasParent(): boolean {
    return !!this.project.parent_id && this.project.parents?.length > 0;
  }

  onTabOrderChanged(event: string[]): void {
    this.projectTemplate.definition.dashboard.header = event;
    this.saveTemplate();
  }

  onTabAdded(event: { tabName: string; menuItemKeys: string[] }): void {
    if (!this.projectTemplate.definition.dashboard.tabs) {
      this.projectTemplate.definition.dashboard.tabs = [];
    }

    const id = guid();

    this.projectTemplate.definition.dashboard.tabs.push({
      id: id,
      name: event.tabName,
      layout: [],
      panels: [],
    });

    this.projectTemplate.definition.dashboard.header = [...event.menuItemKeys, id];

    this.saveTemplate();
  }

  onTabRemoved(event: { id: string; displayText: string }): void {
    this.confirmService
      .openConfirmationModal({
        message: `Are you sure that you wish to remove the tab: ${event.displayText}?`,
      })
      .pipe(filter((res: boolean) => res))
      .subscribe(() => {
        this.projectTemplate.definition.dashboard.tabs = this.projectTemplate.definition.dashboard.tabs.filter(
          ({ id }) => id !== event.id,
        );
        this.projectTemplate.definition.dashboard.header = this.projectTemplate.definition.dashboard.header.filter(
          (menuItemKey) => menuItemKey !== event.id,
        );
        this.saveTemplate();
      });
  }

  onCreateCustomObjectsTabs(): void {
    this.hasCustomObjectsEnabled$
      .pipe(
        untilComponentDestroyed(this),
        filter((hasCustomObjectsEnabled) => hasCustomObjectsEnabled),
        tap(() => this.store.dispatch(new ObjectsManagerActions.Get())),
        switchMap(() => this.get$.pipe(debounceTime(100))),
      )
      .subscribe((objects) => {
        if (!objects?.length) return;
        const parentObject = objects.find((object) => object.name === RelatedToType.Project);
        this.customObjectsTabs = this.store.selectSnapshot(ObjectsManagerState.getLinkedObjects(parentObject));
        this.handleCOLinkMenuResponsiveness();
      });
  }

  selectCustomObjectTab = (tab: CustomObject, clicked?: boolean): void => {
    this.selectedCustomObjectTab = tab;

    if (clicked) {
      this._router.navigate([this.projectRoute + '/custom_object', this.selectedCustomObjectTab.id], {
        relativeTo: this._activatedRoute,
        queryParams: { related_to_type: this.relatedToTypeProject, related_to_id: this.project.id },
      });
    }
  };

  hasCustomObjectPermission(): boolean {
    if (this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_PERMISSIONS))) {
      return true;
    }

    return this.store.selectSnapshot(UserState.hasPermission(Permission.CUSTOM_OBJECTS_TAB));
  }

  hasPermission(permission: Permission): boolean {
    if (this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_PERMISSIONS))) {
      return true;
    }

    return this.store.selectSnapshot(UserState.hasPermission(permission));
  }

  canEditDeal(): [NewPermission, boolean] | boolean {
    if (this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_PERMISSIONS))) {
      return [NewPermission.DEALS_EDIT, this.project.permissions?.is_editable];
    }

    return true;
  }

  canDeleteDeal(): boolean | Permission {
    if (this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_PERMISSIONS))) {
      return this.project.permissions?.is_deletable;
    }

    return Permission.DEALS_DELETE;
  }

  public onFileSelected(event: Event & { target: { files: File[] } }): void {
    let file = event.target.files[0];
    this._projectsService
      .uploadOm(file)
      .pipe(untilComponentDestroyed(this))
      .subscribe((document) => {
        this.callEthanAI(document);
      });
  }

  get canAccessEthan(): boolean {
    if (this.account) {
      return this.account.features?.includes(FeatureFlag.ETHAN);
    }
  }

  public callEthanAI(document): void {
    this.ui.isAiLoading = true;
    this._changeDetectorRef.markForCheck();
    this._snackBar.open(`Calling Ethan AI to automatically extract data from the file.`, '', {
      duration: 3000,
    });

    let inputForFields = [];

    let templateFields = this._projectsService.extractFormFieldsFromTemplate(this.projectTemplate);

    this.fieldDefs.forEach((fieldDef) => {
      if (!fieldDef?.meta?.readonly) {
        if (templateFields.length == 0 || templateFields.includes(fieldDef?.name)) {
          inputForFields.push({ name: fieldDef.name, type: fieldDef.type, label: fieldDef.label });
        }
      }
    });

    this.ethanAbilities
      .extractDataFromDocumentAbility({
        inputs: {
          url: decodeURIComponent(document.pdf_url),
          list_of_fields: inputForFields,
        },
      })
      .subscribe(
        (data) => {
          console.log(data);
          const dialogRef = this._matDialog.open(EthanFormFieldSelectionModal, {
            disableClose: true,
            panelClass: 'mailbox-compose-dialog',
            data: { results: data?.output, inputFields: inputForFields },
          });
          dialogRef.afterClosed().subscribe((result) => {
            if (result.model) {
              let projectModel = { ...this.project, ...result.model };
              this._projectsService.saveProject(projectModel, null, false).subscribe((project) => {
                this._snackBar.open('Data saved successfully.', '', {
                  duration: 3000,
                });
              });
            }
          });
          this.ui.isAiLoading = false;
          this._changeDetectorRef.markForCheck();
        },
        () => {
          this.ui.isAiLoading = false;
        },
      );
  }

  private getTabsFromTemplate(projectTemplate: ProjectTemplate<any>) {
    const tabs = projectTemplate.definition?.dashboard?.tabs;
    return Array.isArray(tabs) ? tabs : [];
  }
}
